import _metadataHelper from "../functions/helpers/metadata.js";
import _ttmlFunc from "../functions/profiles/ttmlGeneral.js";
import _rosetta from "../functions/profiles/imscRosetta.js";
import _Event from "../classes/event.js";
import { XMLParser as _XMLParser } from "fast-xml-parser";
import _eol from "eol";
import _convertToPlainText from "../functions/quill/convertToPlainText.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const convertToPlainText = _convertToPlainText;
const eol = _eol;
const XMLParser = _XMLParser;
const Event = _Event;
const rosetta = _rosetta;
const ttmlFunc = _ttmlFunc;
const metadataHelper = _metadataHelper;

/**
 * IMSC Rosetta profile — practical IMSC 1.2-compatible subtitle format (.imscr)
 * Spec: https://github.com/imsc-rosetta/imsc-rosetta-specification
 *
 * Timing is on <div> (HH:MM:SS.TTT). Fixed style IDs. Supports Japanese rubies,
 * vertical text, boxing, outline/dropshadow.
 */
export default {
  decode: function (input, options) {
    const events = [];
    const xmlToJson = new XMLParser({
      numberParseOptions: {
        skipLike: /[0-9]+/
      },
      ignoreAttributes: false,
      trimValues: false,
      isArray: name => ["style", "region", "div", "p", "span"].includes(name)
    });
    let fileJson = xmlToJson.parse(input);
    if (!fileJson.tt) {
      fileJson = {
        tt: fileJson
      };
    }
    const styles = rosetta.ensureArray(fileJson.tt?.head?.styling?.style);
    const regions = rosetta.ensureArray(fileJson.tt?.head?.layout?.region);
    const quant = rosetta.parseQuantisationRegion(styles);
    const divs = rosetta.ensureArray(fileJson.tt?.body?.div);
    const winHeight = options.window?.height || 15;

    // Resolve _d_default → default outline mode
    let fileOutlineMode = "outline";
    const dDefaultMeta = styles.find(s => s["@_xml:id"] === "_d_default");
    if (dDefaultMeta) {
      fileOutlineMode = rosetta.outlineModeFromStyles(rosetta.parseStyleList(dDefaultMeta["@_style"])) || "outline";
    }
    divs.forEach(div => {
      const ccEvent = new Event();
      const begin = div["@_begin"];
      const end = div["@_end"];
      if (!begin || !end) {
        return;
      }
      ccEvent.start = tcLib.parseTcToSec(begin, options.frameRate, options.dropFrame, true);
      ccEvent.end = tcLib.parseTcToSec(end, options.frameRate, options.dropFrame, true);
      const regionId = div["@_region"];
      const region = regions.find(r => r["@_xml:id"] === regionId);
      const pos = rosetta.regionToPosition(region, quant, winHeight);
      ccEvent.xPos = pos.xPos;
      ccEvent.yPos = pos.yPos;
      ccEvent.writingMode = pos.writingMode;
      const divStyles = rosetta.parseStyleList(div["@_style"]);
      ccEvent.forced = divStyles.includes("d_forced") || div["@_itts:forcedDisplay"] === "true";
      ccEvent.outlineMode = rosetta.outlineModeFromStyles(divStyles) || fileOutlineMode;
      if (div["@_rosetta:comment"]) {
        ccEvent.notes = div["@_rosetta:comment"];
      } else if (div.metadata && div.metadata["@_rosetta:comment"]) {
        ccEvent.notes = div.metadata["@_rosetta:comment"];
      }
      const paragraphs = rosetta.ensureArray(div.p);
      ccEvent.boxing = rosetta.boxingFromParagraphAndSpans(paragraphs);
      ccEvent.forced = ccEvent.forced || paragraphs.some(p => p["@_itts:forcedDisplay"] === "true");
      const lineHtmls = [];
      let alignment = "center";
      let hasShear = false;
      let hasRuby = false;
      paragraphs.forEach((p, pIndex) => {
        const pStyles = rosetta.parseStyleList(p["@_style"]);
        const alignInfo = rosetta.alignmentFromStyles(pStyles);
        if (pIndex === 0) {
          alignment = alignInfo.alignment;
        }
        if (pStyles.includes("p_shear")) {
          hasShear = true;
        }
        if (pStyles.includes("p_rb_res_outside") || pStyles.some(s => s.startsWith("s_rb_"))) {
          hasRuby = true;
        }
        let fragment = rosetta.spansToHtml(p);
        // Multi-p with different alignments: keep as separate lines
        lineHtmls.push(fragment);
      });
      if (hasShear) {
        ccEvent.tags = [...(ccEvent.tags || []), "shear"];
      }
      if (hasRuby) {
        ccEvent.tags = [...(ccEvent.tags || []), "ruby"];
      }
      ccEvent.alignment = alignment;
      const joined = lineHtmls.join("\n");
      ccEvent.text = rosetta.convertToHtml(joined, [rosetta.quillClasses.align[alignment] || rosetta.quillClasses.align.center]);
      events.push(ccEvent);
    });

    /* Pass file level styling to postProcess.decode so it can be stored as Event Group metadata */
    const quantStyle = styles.find(s => s["@_xml:id"] === "_r_quantisationregion");
    events._sourceMeta = {
      defaultOutline: dDefaultMeta ? fileOutlineMode : undefined,
      fontSizeRh: quantStyle && quantStyle["@_tts:fontSize"] ? String(quant.fontSizeRh) : undefined
    };
    return events;
  },
  encode: function (eventGroup, options) {
    const encodingOptions = getFormatOptions(options.formatOptions);
    const lang = encodingOptions["Language Code"] || "en";
    const fontSizeRh = encodingOptions["Font Size"] || "5.333";
    const defaultOutline = encodingOptions["Default Outline"] || "outline";
    const outlineStyle = defaultOutline === "drop" ? "d_drop" : defaultOutline === "none" ? "d_none" : "d_outline";
    const frameRateMultiplier = ttmlFunc.frameRateMultiplierMap[options.frameRate] || "1 1";
    const frameRate = ttmlFunc.frameRateMap[options.frameRate] || Math.round(options.frameRate) || 25;
    const quant = {
      ...rosetta.DEFAULT_QUANT,
      fontSizeRh: parseFloat(fontSizeRh) || 5.333
    };
    const regionMap = new Map();
    const usedStyles = new Set(["d_outline", "s_outlineblack", "s_fg_white", "p_al_center"]);
    let body = "";
    let eventIndex = 0;
    eventGroup.events.forEach(event => {
      if (!event.text || !convertToPlainText(event.text).trim()) {
        return;
      }
      eventIndex += 1;
      const plain = convertToPlainText(event.text);
      const lineCount = Math.max(1, eol.split(plain).filter(l => l.trim()).length);
      const regionInfo = rosetta.positionToRegion(event, quant, lineCount);
      if (regionInfo.writingMode === "tbrl") {
        usedStyles.add("r_vertical");
        usedStyles.add("_r_vertical");
      }
      let regionId;
      if (!regionMap.has(regionInfo.regionKey)) {
        regionId = `R${regionMap.size}`;
        regionMap.set(regionInfo.regionKey, {
          id: regionId,
          ...regionInfo.markup
        });
      } else {
        regionId = regionMap.get(regionInfo.regionKey).id;
      }
      const alignStyle = rosetta.ALIGN_TO_STYLE[event.alignment] || "p_al_center";
      usedStyles.add(alignStyle);
      let divStyles = ["d_default"];
      if (event.forced) {
        divStyles.push("d_forced");
        usedStyles.add("d_forced");
      }
      if (event.outlineMode === "drop") {
        divStyles.push("d_drop");
        usedStyles.add("d_drop");
        usedStyles.add("s_dropblack");
      } else if (event.outlineMode === "none") {
        divStyles.push("d_none");
        usedStyles.add("d_none");
        usedStyles.add("s_noneblack");
      } else if (event.outlineMode === "outline") {
        divStyles.push("d_outline");
      }
      let bgStyle = null;
      if (event.boxing === "boxed" || event.boxing === "stripe") {
        bgStyle = "ps_bg_boxedblack";
        usedStyles.add(bgStyle);
      } else if (event.boxing === "ghostBoxed" || event.boxing === "ghostStripe") {
        bgStyle = "ps_bg_ghostboxedblack";
        usedStyles.add(bgStyle);
      }
      const isStripe = event.boxing === "stripe" || event.boxing === "ghostStripe";
      const hasRuby = rosetta.rubyMarkup.hasRuby(event.text) || (event.tags || []).includes("ruby");
      const hasShear = (event.tags || []).includes("shear");
      const spanHtml = rosetta.htmlToRosettaSpans(event.text, {
        defaultBgStyle: !isStripe ? bgStyle : null
      });
      rosetta.collectUsedStylesFromSpans(spanHtml).forEach(s => usedStyles.add(s));
      let pStyles = ["p_font2"];
      if (alignStyle !== "p_al_center") {
        pStyles.push(alignStyle);
      }
      if (hasRuby) {
        pStyles.push("p_rb_res_outside");
        usedStyles.add("p_rb_res_outside");
      }
      if (hasShear) {
        pStyles.push("p_shear");
        usedStyles.add("p_shear");
      }
      if (isStripe && bgStyle) {
        pStyles.push(bgStyle);
      }
      const begin = rosetta.clockTimeFromSec(event.start);
      const end = rosetta.clockTimeFromSec(event.end);

      // Prefer single <p> (required for Japanese rubies); multi-line via <span><br/></span>
      body += eol.after(`  <div xml:id="e_${eventIndex}" region="${regionId}" begin="${begin}" end="${end}" style="${divStyles.join(" ")}">`);
      body += eol.after(`   <p style="${pStyles.join(" ")}"${ttmlFunc.forcedDisplayAttr(event)}>${spanHtml}</p>`);
      body += eol.after(`  </div>`);
    });
    let regionsXml = "";
    regionMap.forEach(region => {
      regionsXml += eol.after(`   <region xml:id="${region.id}" tts:origin="${region.origin}" tts:extent="${region.extent}" tts:displayAlign="${region.displayAlign}" style="${region.style}"/>`);
    });
    if (!regionsXml) {
      regionsXml = eol.after(`   <region xml:id="R0" tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="after" style="r_default"/>`);
      usedStyles.add("r_default");
    }
    const styling = rosetta.buildStyleBlock(usedStyles, {
      fontSizeRh,
      defaultOutline: outlineStyle
    });
    let output = eol.after(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`);
    output += eol.after(`<tt xmlns="http://www.w3.org/ns/ttml" xmlns:ttm="http://www.w3.org/ns/ttml#metadata" xmlns:tts="http://www.w3.org/ns/ttml#styling" xmlns:ttp="http://www.w3.org/ns/ttml#parameter" xmlns:xml="http://www.w3.org/XML/1998/namespace" xmlns:ebutts="urn:ebu:tt:style" xmlns:itts="http://www.w3.org/ns/ttml/profile/imsc1#styling" xmlns:rosetta="https://github.com/imsc-rosetta/specification" ttp:timeBase="media" ttp:cellResolution="30 15" xml:space="preserve" ttp:frameRate="${frameRate}" ttp:frameRateMultiplier="${frameRateMultiplier}" xml:lang="${lang}">`);
    output += eol.after(` <head>`);
    output += eol.after(`  <metadata>`);
    output += eol.after(`   <rosetta:format>imsc-rosetta</rosetta:format>`);
    output += eol.after(`   <rosetta:version>0.0.0</rosetta:version>`);
    if (encodingOptions["Program Name"]) {
      output += eol.after(`   <ttm:title>${rosetta.rubyMarkup.escapeXml(encodingOptions["Program Name"])}</ttm:title>`);
    }
    output += eol.after(`  </metadata>`);
    output += eol.after(`  <styling>`);
    output += eol.after(styling);
    output += eol.after(`  </styling>`);
    output += eol.after(`  <layout>`);
    output += regionsXml;
    output += eol.after(`  </layout>`);
    output += eol.after(` </head>`);
    output += eol.after(` <body>`);
    output += body;
    output += eol.after(` </body>`);
    output += `</tt>`;
    return output;
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return eol.lf(input);
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      const meta = eventGroup.events && eventGroup.events._sourceMeta;
      if (meta) {
        metadataHelper.setMetadata(eventGroup, "defaultOutline", meta.defaultOutline, {
          source: "_d_default"
        });
        metadataHelper.setMetadata(eventGroup, "fontSizeRh", meta.fontSizeRh, {
          source: "_r_quantisationregion"
        });
        delete eventGroup.events._sourceMeta;
      }
      return eventGroup;
    }
  }
};